import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AllCommunityModule,
  type CellFocusedEvent,
  type CellValueChangedEvent,
  type ColDef,
  type GridApi,
  type GridReadyEvent,
  ModuleRegistry,
  type SelectionChangedEvent,
} from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { useFormContext } from 'react-hook-form';
import type { DynamicGridColumnConfig, Question, SelectOption } from '../../config/types';
import { parseTabularClipboard } from '../../utils/bulk-table/clipboard';
import Details from './Details';
import ErrorMessage from './ErrorMessage';

ModuleRegistry.registerModules([AllCommunityModule]);

type DynamicGridRow = Record<string, string> & {
  __rowId?: string;
  __dynamicGridSpecialRow?: 'total';
};

type PasteArgs = {
  columns: DynamicGridColumnConfig[];
  rows: Record<string, unknown>[];
  text: string;
  startRowIndex: number;
  startColumnIndex: number;
  maxRows?: number;
};

const isOptionGroup = (
  option: SelectOption,
): option is Exclude<SelectOption, string> & {
  label: string;
  options: string[];
} => typeof option === 'object' && 'label' in option && 'options' in option;

const isOptionDefault = (
  option: SelectOption,
): option is Exclude<SelectOption, string> & {
  option: string;
  value?: string;
} => typeof option === 'object' && 'option' in option;

const normaliseNumberText = (value: unknown): string => {
  const raw = String(value ?? '').trim();
  if (!raw) return '';
  return raw.replace(/[\u00a3,\s]/g, '');
};

const formatNumberText = (value: unknown): string => {
  const raw = normaliseNumberText(value);
  if (!raw) return '';
  const numericValue = Number(raw);
  if (!Number.isFinite(numericValue)) return String(value ?? '');
  return numericValue.toLocaleString('en-GB', { maximumFractionDigits: 20 });
};

const formatDateText = (value: unknown): string => {
  const raw = String(value ?? '').trim();
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!isoMatch) return raw;
  return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
};

const normaliseDateText = (value: unknown): string => {
  const raw = String(value ?? '').trim();
  const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!slashMatch) return raw;
  return `${slashMatch[3]}-${slashMatch[2].padStart(2, '0')}-${slashMatch[1].padStart(2, '0')}`;
};

const getOptionPairs = (options?: SelectOption[]): { value: string; label: string }[] => {
  if (!Array.isArray(options)) return [];
  return options.flatMap((option) => {
    if (typeof option === 'string') return [{ value: option, label: option }];
    if (isOptionGroup(option)) {
      return option.options.map((item) => ({ value: item, label: item }));
    }
    if (isOptionDefault(option)) {
      return [{ value: option.value ?? option.option, label: option.option }];
    }
    return [];
  });
};

const normaliseCellValue = (column: DynamicGridColumnConfig, value: unknown): string => {
  if (value === undefined || value === null) return '';
  if (column.type === 'number') return normaliseNumberText(value);
  if (column.type === 'date') return normaliseDateText(value);
  return String(value);
};

const normalisePasteCellValue = (column: DynamicGridColumnConfig, value: string): string => {
  const rawValue = value.trim();
  if (column.type === 'select') {
    const option = getOptionPairs(column.options).find(
      (pair) =>
        pair.value.toLowerCase() === rawValue.toLowerCase() ||
        pair.label.toLowerCase() === rawValue.toLowerCase(),
    );
    return option?.value ?? rawValue;
  }
  return normaliseCellValue(column, rawValue);
};

const createBlankRow = (columns: DynamicGridColumnConfig[]): DynamicGridRow =>
  columns.reduce<DynamicGridRow>((row, column) => {
    row[column.field] = '';
    return row;
  }, {});

const addRowIds = (rows: DynamicGridRow[]): DynamicGridRow[] =>
  rows.map((row, index) => ({
    ...row,
    __rowId: row.__rowId ?? `dynamic-grid-row-${index + 1}`,
  }));

export const normaliseDynamicGridRows = (
  columns: DynamicGridColumnConfig[],
  rows?: Record<string, unknown>[],
  minRows = 0,
): DynamicGridRow[] => {
  const sourceRows = Array.isArray(rows) ? rows : [];
  const normalisedRows = sourceRows.map((sourceRow) => {
    const row = createBlankRow(columns);
    columns.forEach((column) => {
      row[column.field] = normaliseCellValue(column, sourceRow[column.field]);
    });
    return row;
  });

  while (normalisedRows.length < minRows) {
    normalisedRows.push(createBlankRow(columns));
  }

  return normalisedRows;
};

const stripDynamicGridMeta = (
  columns: DynamicGridColumnConfig[],
  rows: DynamicGridRow[],
): Record<string, string>[] =>
  rows
    .filter((row) => row.__dynamicGridSpecialRow !== 'total')
    .map((row) =>
      columns.reduce<Record<string, string>>((out, column) => {
        out[column.field] = row[column.field] ?? '';
        return out;
      }, {}),
    );

const parseStoredRows = (
  rawValue: unknown,
  columns: DynamicGridColumnConfig[],
  fallbackRows: Record<string, unknown>[] | undefined,
  minRows: number,
): DynamicGridRow[] => {
  if (Array.isArray(rawValue)) {
    return addRowIds(
      normaliseDynamicGridRows(columns, rawValue as Record<string, unknown>[], minRows),
    );
  }
  if (typeof rawValue === 'string' && rawValue.trim()) {
    try {
      const parsed = JSON.parse(rawValue);
      if (Array.isArray(parsed)) {
        return addRowIds(
          normaliseDynamicGridRows(columns, parsed as Record<string, unknown>[], minRows),
        );
      }
    } catch (_err) {
      return addRowIds(normaliseDynamicGridRows(columns, fallbackRows, minRows));
    }
  }
  return addRowIds(normaliseDynamicGridRows(columns, fallbackRows, minRows));
};

export const parseDynamicGridTsv = parseTabularClipboard;

export const applyDynamicGridPaste = ({
  columns,
  rows,
  text,
  startRowIndex,
  startColumnIndex,
  maxRows,
}: PasteArgs): DynamicGridRow[] => {
  const parsedRows = parseTabularClipboard(text);
  const nextRows = normaliseDynamicGridRows(columns, rows);
  const rowLimit = maxRows ?? Number.POSITIVE_INFINITY;

  parsedRows.forEach((parsedRow, rowOffset) => {
    const targetRowIndex = startRowIndex + rowOffset;
    if (targetRowIndex >= rowLimit) return;

    while (nextRows.length <= targetRowIndex && nextRows.length < rowLimit) {
      nextRows.push(createBlankRow(columns));
    }

    const targetRow = nextRows[targetRowIndex];
    if (!targetRow) return;

    parsedRow.forEach((cellValue, columnOffset) => {
      const targetColumn = columns[startColumnIndex + columnOffset];
      if (!targetColumn || targetColumn.readOnly) return;
      targetRow[targetColumn.field] = normalisePasteCellValue(targetColumn, cellValue);
    });
  });

  return nextRows;
};

export const buildDynamicGridTotalsRow = (
  columns: DynamicGridColumnConfig[],
  rows: Record<string, unknown>[],
): DynamicGridRow | undefined => {
  if (!columns.some((column) => column.total)) return undefined;

  const totalRow = columns.reduce<DynamicGridRow>(
    (row, column) => {
      row[column.field] = '';
      if (column.total) {
        const total = rows.reduce((sum, currentRow) => {
          const numericValue = Number(normaliseNumberText(currentRow[column.field]));
          return Number.isFinite(numericValue) ? sum + numericValue : sum;
        }, 0);
        row[column.field] = total.toLocaleString('en-GB', { maximumFractionDigits: 20 });
      }
      return row;
    },
    { __dynamicGridSpecialRow: 'total', __rowId: 'dynamic-grid-total' },
  );

  return totalRow;
};

const buildClipboardText = (
  columns: DynamicGridColumnConfig[],
  rows: Record<string, unknown>[],
): string =>
  rows
    .map((row) =>
      columns
        .map((column) => {
          const value = String(row[column.field] ?? '');
          if (value.includes('\t') || value.includes('\n') || value.includes('"')) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        })
        .join('\t'),
    )
    .join('\n');

const getDisplayValue = (column: DynamicGridColumnConfig, value: unknown): string => {
  if (column.type === 'select') {
    const option = getOptionPairs(column.options).find((pair) => pair.value === value);
    return option?.label ?? String(value ?? '');
  }
  if (column.type === 'number') {
    const formatted = formatNumberText(value);
    return formatted ? `${column.prefix ?? ''}${formatted}${column.suffix ?? ''}` : '';
  }
  if (column.type === 'date') return formatDateText(value);
  return String(value ?? '');
};

type Props = {
  question: Question;
  disabled?: boolean;
  anchorId?: string;
  detailsHeading?: string;
  detailsContent?: string;
};

export function DynamicGrid({
  question,
  disabled,
  anchorId,
  detailsHeading,
  detailsContent,
}: Props) {
  const { register, getFieldState, getValues, setValue } = useFormContext();
  const { error } = getFieldState(question.name);
  const config = question.dynamicGrid;
  const columns = useMemo(() => config?.columns ?? [], [config?.columns]);
  const minRows = config?.minRows ?? 0;
  const maxRows = config?.maxRows;
  const isDisabled = Boolean(disabled || question.readOnly);
  const gridApiRef = useRef<GridApi<DynamicGridRow> | null>(null);
  const focusedCellRef = useRef<{ rowIndex: number; columnIndex: number }>({
    rowIndex: 0,
    columnIndex: 0,
  });

  const initialRows = useMemo(
    () => parseStoredRows(getValues(question.name), columns, config?.rows, minRows),
    [columns, config?.rows, getValues, minRows, question.name],
  );

  const [rows, setRows] = useState<DynamicGridRow[]>(initialRows);
  const [undoStack, setUndoStack] = useState<DynamicGridRow[][]>([]);
  const [redoStack, setRedoStack] = useState<DynamicGridRow[][]>([]);
  const [selectedCount, setSelectedCount] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');

  const commitRows = useCallback(
    (nextRows: DynamicGridRow[], options: { saveUndo?: boolean; shouldDirty?: boolean } = {}) => {
      const rowsWithIds = addRowIds(nextRows);
      if (options.saveUndo) {
        setUndoStack((previous) => [...previous.slice(-49), rows]);
        setRedoStack([]);
      }
      setRows(rowsWithIds);
      setValue(question.name, stripDynamicGridMeta(columns, rowsWithIds), {
        shouldDirty: options.shouldDirty ?? true,
        shouldValidate: true,
      });
    },
    [columns, question.name, rows, setValue],
  );

  useEffect(() => {
    register(question.name, {
      validate: (value: unknown) => {
        if (!question.rules?.required) return true;
        const submittedRows = Array.isArray(value)
          ? (value as Record<string, unknown>[])
          : stripDynamicGridMeta(columns, rows);
        if (submittedRows.length === 0) return `${question.title} is required`;
        const requiredColumns = columns.filter((column) => column.required);
        for (const row of submittedRows) {
          for (const column of requiredColumns) {
            if (!String(row[column.field] ?? '').trim()) {
              return `${column.headerName} is required`;
            }
          }
        }
        return true;
      },
    });
  }, [columns, question.name, question.rules?.required, question.title, register, rows]);

  useEffect(() => {
    setValue(question.name, stripDynamicGridMeta(columns, rows), {
      shouldDirty: false,
      shouldValidate: false,
    });
    // Initial sync only. Later updates go through commitRows.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columnDefs = useMemo<ColDef<DynamicGridRow>[]>(
    () =>
      columns.map((column) => {
        const colDef: ColDef<DynamicGridRow> = {
          field: column.field,
          headerName: column.headerName,
          editable: (params) =>
            !isDisabled && !column.readOnly && params.data?.__dynamicGridSpecialRow !== 'total',
          minWidth: column.minWidth ?? 140,
          width: column.width,
          maxWidth: column.maxWidth,
          resizable: true,
          sortable: false,
          valueFormatter: (params) => getDisplayValue(column, params.value),
          valueParser: (params) => normaliseCellValue(column, params.newValue),
        };

        if (column.type === 'select') {
          colDef.cellEditor = 'agSelectCellEditor';
          colDef.cellEditorParams = {
            values: getOptionPairs(column.options).map((option) => option.value),
          };
        } else if (column.type === 'number') {
          colDef.cellEditor = 'agNumberCellEditor';
        } else if (column.type === 'date') {
          colDef.cellEditor = 'agDateStringCellEditor';
        } else {
          colDef.cellEditor = 'agTextCellEditor';
        }

        if (column.readOnly) {
          colDef.cellClass = 'dynamic-grid__cell--read-only';
        }

        return colDef;
      }),
    [columns, isDisabled],
  );

  const totalsRow = useMemo(
    () => (config?.includeTotals === false ? undefined : buildDynamicGridTotalsRow(columns, rows)),
    [columns, config?.includeTotals, rows],
  );

  const handleCellValueChanged = (event: CellValueChangedEvent<DynamicGridRow>) => {
    if (!event.data || event.data.__dynamicGridSpecialRow === 'total') return;
    const rowIndex = event.node.rowIndex;
    if (typeof rowIndex !== 'number') return;
    const field = event.colDef.field;
    const column = columns.find((currentColumn) => currentColumn.field === field);
    if (!field || !column) return;

    const previousRows = rows.map((row, index) =>
      index === rowIndex ? { ...row, [field]: normaliseCellValue(column, event.oldValue) } : row,
    );
    const nextRows = rows.map((row, index) =>
      index === rowIndex
        ? { ...row, ...event.data, [field]: normaliseCellValue(column, event.newValue) }
        : row,
    );

    setUndoStack((previous) => [...previous.slice(-49), previousRows]);
    setRedoStack([]);
    commitRows(nextRows);
  };

  const handleAddRow = () => {
    if (maxRows !== undefined && rows.length >= maxRows) return;
    commitRows([...rows, createBlankRow(columns)], { saveUndo: true });
    setStatusMessage('Added row.');
  };

  const handleDeleteRows = () => {
    const selectedRows = gridApiRef.current?.getSelectedRows() ?? [];
    if (selectedRows.length === 0) return;
    const selectedIds = new Set(selectedRows.map((row) => row.__rowId));
    const nextRows = rows.filter((row) => !selectedIds.has(row.__rowId));
    commitRows(
      nextRows.length >= minRows ? nextRows : normaliseDynamicGridRows(columns, nextRows, minRows),
      {
        saveUndo: true,
      },
    );
    gridApiRef.current?.deselectAll();
    setSelectedCount(0);
    setStatusMessage(`Deleted ${selectedRows.length} row${selectedRows.length === 1 ? '' : 's'}.`);
  };

  const handleUndo = () => {
    const previousRows = undoStack[undoStack.length - 1];
    if (!previousRows) return;
    setUndoStack((previous) => previous.slice(0, -1));
    setRedoStack((previous) => [...previous, rows]);
    commitRows(previousRows, { shouldDirty: true });
  };

  const handleRedo = () => {
    const nextRows = redoStack[redoStack.length - 1];
    if (!nextRows) return;
    setRedoStack((previous) => previous.slice(0, -1));
    setUndoStack((previous) => [...previous, rows]);
    commitRows(nextRows, { shouldDirty: true });
  };

  const handlePaste = async () => {
    const text = await navigator.clipboard?.readText?.();
    if (!text) return;
    const nextRows = applyDynamicGridPaste({
      columns,
      rows,
      text,
      startRowIndex: focusedCellRef.current.rowIndex,
      startColumnIndex: focusedCellRef.current.columnIndex,
      maxRows,
    });
    commitRows(nextRows, { saveUndo: true });
    setStatusMessage('Pasted rows from spreadsheet.');
  };

  const handleCopy = async () => {
    const selectedRows = gridApiRef.current?.getSelectedRows() ?? [];
    const rowsToCopy = selectedRows.length ? selectedRows : rows;
    await navigator.clipboard?.writeText?.(
      buildClipboardText(columns, stripDynamicGridMeta(columns, rowsToCopy)),
    );
    setStatusMessage(
      `Copied ${rowsToCopy.length} row${rowsToCopy.length === 1 ? '' : 's'} to clipboard.`,
    );
  };

  const handleWrapperPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    const text = event.clipboardData.getData('text/plain');
    if (!text) return;
    event.preventDefault();
    const nextRows = applyDynamicGridPaste({
      columns,
      rows,
      text,
      startRowIndex: focusedCellRef.current.rowIndex,
      startColumnIndex: focusedCellRef.current.columnIndex,
      maxRows,
    });
    commitRows(nextRows, { saveUndo: true });
    setStatusMessage('Pasted rows from clipboard.');
  };

  const onGridReady = (event: GridReadyEvent<DynamicGridRow>) => {
    gridApiRef.current = event.api;
  };

  const onSelectionChanged = (event: SelectionChangedEvent<DynamicGridRow>) => {
    setSelectedCount(event.api.getSelectedRows().length);
  };

  const handleCellFocused = (event: CellFocusedEvent<DynamicGridRow>) => {
    if (typeof event.rowIndex !== 'number' || !event.column) return;
    const focusedColumnId =
      typeof event.column === 'string' ? event.column : event.column.getColId();
    const columnIndex = columns.findIndex((column) => column.field === focusedColumnId);
    focusedCellRef.current = {
      rowIndex: event.rowIndex,
      columnIndex: Math.max(0, columnIndex),
    };
  };

  if (!config || columns.length === 0) {
    return null;
  }

  const gridLabel = anchorId ?? `${question.name}-dynamic-grid`;

  return (
    <div className="dynamic-grid" onPaste={handleWrapperPaste}>
      {error && <ErrorMessage error={error} />}
      {detailsHeading && <Details heading={detailsHeading} content={detailsContent} />}

      <div
        className="ag-theme-quartz"
        aria-label={question.title}
        style={{ width: '100%', height: config.height ?? 360 }}
        id={gridLabel}
      >
        <AgGridReact<DynamicGridRow>
          rowData={rows}
          columnDefs={columnDefs}
          pinnedBottomRowData={totalsRow ? [totalsRow] : undefined}
          onGridReady={onGridReady}
          onCellValueChanged={handleCellValueChanged}
          onSelectionChanged={onSelectionChanged}
          onCellFocused={handleCellFocused}
          rowSelection={{ mode: 'multiRow', checkboxes: true, headerCheckbox: true } as any}
          suppressRowClickSelection={false}
          stopEditingWhenCellsLoseFocus
          defaultColDef={{
            editable: !isDisabled,
            resizable: true,
            suppressMovable: true,
            wrapHeaderText: true,
            autoHeaderHeight: true,
          }}
        />
      </div>

      <div className="govuk-button-group govuk-!-margin-top-3">
        <button
          type="button"
          className="govuk-button govuk-button--secondary"
          onClick={handleAddRow}
          disabled={isDisabled || (maxRows !== undefined && rows.length >= maxRows)}
        >
          {config.addButtonText ?? 'Add row'}
        </button>
        <button
          type="button"
          className="govuk-button govuk-button--secondary"
          onClick={handleUndo}
          disabled={isDisabled || undoStack.length === 0}
        >
          {config.undoButtonText ?? 'Undo'}
        </button>
        <button
          type="button"
          className="govuk-button govuk-button--secondary"
          onClick={handleRedo}
          disabled={isDisabled || redoStack.length === 0}
        >
          {config.redoButtonText ?? 'Redo'}
        </button>
        <button
          type="button"
          className="govuk-button govuk-button--secondary"
          onClick={handlePaste}
          disabled={isDisabled}
        >
          {config.pasteButtonText ?? 'Paste from spreadsheet'}
        </button>
        <button type="button" className="govuk-button govuk-button--secondary" onClick={handleCopy}>
          {config.copyButtonText ?? 'Copy'}
        </button>
        <button
          type="button"
          className="govuk-button govuk-button--warning"
          onClick={handleDeleteRows}
          disabled={isDisabled || selectedCount === 0}
        >
          {config.deleteButtonText ?? 'Delete selected rows'}
        </button>
      </div>

      <div className="govuk-visually-hidden" aria-live="polite">
        {statusMessage}
      </div>
    </div>
  );
}

export default DynamicGrid;
